import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { NOTIF_SESSION_PREFIX, useNotifications } from "@/contexts/NotificationsContext";
import { Button } from "@/components/ui/button";
import { iconFor, timeAgo } from "@/lib/notifications";

/**
 * "While you were away" digest, shown once per sign-in to every signed-in role.
 *
 * The slide-in popups (NotificationPopups) fire on the same first poll, but they
 * clear themselves after eight seconds — someone who lands mid-onboarding, or
 * who is still reading the announcement dialog, never actually sees them, and
 * the ids are recorded as announced so they never pop again. This dialog is the
 * durable surface: it stays until dismissed, and it clears the slide-in queue on
 * open so the same unread isn't presented twice at once.
 */

/** Per-tab, per-user flag so the digest opens on sign-in only — not on every
 *  route change. Keyed by user id so an admin and a buyer signing in one after
 *  the other each get their own. Shares the notifications prefix, so signing
 *  out sweeps it. */
const SHOWN_KEY = `${NOTIF_SESSION_PREFIX}login-digest`;
/** Rows rendered before the list falls back to a "see all" pointer at the bell. */
const MAX_LISTED = 6;

function alreadyShown(userId: string): boolean {
  try {
    return window.sessionStorage.getItem(`${SHOWN_KEY}.${userId}`) === "1";
  } catch {
    // Private mode / quota — worst case the digest reopens on a reload.
    return false;
  }
}

function markShown(userId: string): void {
  try {
    window.sessionStorage.setItem(`${SHOWN_KEY}.${userId}`, "1");
  } catch {
    /* nothing to persist */
  }
}

export default function NotificationLoginPopup() {
  const { user } = useAuth();
  const { items, loading, markRead, markAllRead, clearPopups } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const userId = user?.id ?? null;

  // Frozen at open time: marking one read must not make rows vanish from under
  // the cursor while the dialog is still up.
  const [listed, setListed] = useState<typeof items>([]);

  const unread = useMemo(() => items.filter((n) => !n.is_read), [items]);

  // The first poll has to land before we can judge "nothing unread" — `loading`
  // starts false, so wait until we've seen it go true and back down again.
  const sawFetch = useRef(false);
  useEffect(() => {
    if (loading) sawFetch.current = true;
  }, [loading]);

  useEffect(() => {
    if (!userId || loading || !sawFetch.current) return;
    if (alreadyShown(userId)) return;
    // Decided once per sign-in: a quiet inbox at login means no digest, and
    // anything arriving later in the session belongs to the slide-ins.
    markShown(userId);
    if (unread.length === 0) return;
    setListed(unread);
    clearPopups();
    setOpen(true);
  }, [userId, loading, unread, clearPopups]);

  if (!open || listed.length === 0) return null;

  const close = () => setOpen(false);

  const openItem = (id: string, link: string | null) => {
    markRead(id);
    close();
    if (link) navigate(link);
  };

  const dismissAll = () => {
    markAllRead();
    close();
  };

  const extra = listed.length - MAX_LISTED;

  return (
    <div className="fixed inset-0 z-[58] flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-md" onClick={close} />

      {/* Dialog — same shell as AnnouncementPopup so the two read as one system. */}
      <div className="public-auth-card relative w-full sm:max-w-[460px] sm:mx-4 rounded-t-[2rem] sm:rounded-[2rem] flex flex-col max-h-[88dvh] overflow-hidden">
        <div className="h-[3px] w-full bg-gradient-to-r from-primary via-primary-light to-primary-glow shrink-0" />

        {/* Drag handle – mobile only */}
        <div className="flex justify-center pt-3 sm:hidden shrink-0">
          <div className="h-1 w-10 rounded-full bg-border/70" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-7 pt-4 sm:pt-6 pb-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[1.1rem] bg-primary/10 text-primary ring-1 ring-primary/10">
              <Bell className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <p className="eyebrow text-primary/80">While you were away</p>
              <h2 className="text-[1.15rem] sm:text-xl font-bold text-foreground leading-snug font-display">
                {listed.length} new notification{listed.length === 1 ? "" : "s"}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close notifications"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 pb-2">
          <ul className="flex flex-col gap-2">
            {listed.slice(0, MAX_LISTED).map((n) => {
              const Icon = iconFor(n.type);
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => openItem(n.id, n.link)}
                    className="flex w-full items-start gap-3 rounded-xl border border-border/70 bg-card/60 p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent/50"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-foreground">{n.title}</span>
                      {n.body && (
                        <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                          {n.body}
                        </span>
                      )}
                      <span className="mt-1 block text-[0.68rem] text-muted-foreground/70">
                        {timeAgo(n.created_at)}
                      </span>
                    </span>
                    {n.link && (
                      <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground/60" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {extra > 0 && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              +{extra} more in the bell menu
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-border/50 bg-background/60 backdrop-blur-sm px-5 sm:px-7 py-4">
          <Button onClick={close} className="w-full rounded-xl">
            View later
          </Button>
          <button
            type="button"
            onClick={dismissAll}
            className="mt-2 w-full text-center text-xs font-semibold text-muted-foreground transition-colors hover:text-primary"
          >
            Mark all as read
          </button>
        </div>
      </div>
    </div>
  );
}
